import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import heroBg from "../../images/bg/hero_bg_new.png";
import heroIcon from "../../images/icon/animated-gif02.gif";

const slides = [
  {
    title: "Eliminate Repetitive Work with Intelligent AI",
    subtitle: <>We put your business operations on autopilot with custom workflows and intelligent integrations. Engineered by <strong>TorqAI®</strong>, these systems streamline your infrastructure, eliminate bottlenecks, and maximize operational capacity for measurable business expansion.</>,
    btn1Text: "Build Your Solution",
    btn2Text: "Explore Our Agents",
    btn1Link: "https://dashboard.torqai.ca/",
    btn2Link: "/products"
  },
  {
    title: "24/7 Autonomous Voice Receptionists",
    subtitle: <>An intelligent AI receptionist powered by <strong>CAPRO®</strong> that manages your front desk completely around the clock. The agent answers incoming calls instantly, handles complex customer inquiries, and books appointments directly into your calendar without a single missed lead.</>,
    btn1Text: "Start Free Trial",
    btn2Text: "View Pricing",
    btn1Link: "https://dashboard.torqai.ca/",
    btn2Link: "/pricing"
  },
  {
    title: "Automate Your Outbound Sales Pipeline",
    subtitle: <>An autonomous outbound and qualification system driven by <strong>SAPRO®</strong> to scale your pipeline. This AI agent contacts fresh leads, filters prospects based on your target buyer criteria, and schedules high-yield meetings directly for your closing team.</>,
    btn1Text: "Start Closing Leads",
    btn2Text: "Check Sales Pricing",
    btn1Link: "https://dashboard.torqai.ca/",
    btn2Link: "/pricing"
  },
  {
    title: "From Job Posting to Shortlist in 2 Days",
    subtitle: <>An automated recruitment solution developed by <strong>HAPRO®</strong> that condenses your hiring timeline from weeks to days. The system screens incoming applications, conducts smart video interviews, and shortlists top talent with zero manual friction.</>,
    btn1Text: "Automate Your Hiring",
    btn2Text: "See All Packages",
    btn1Link: "https://dashboard.torqai.ca/",
    btn2Link: "/pricing"
  }
];

const HeroSection: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [animate, setAnimate] = useState(true);

  // Auto-slide every 8 seconds
  useEffect(() => {
    const slideTimer = setInterval(() => {
      setAnimate(false);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
        setAnimate(true);
      }, 600); // Wait for fade out to complete before swapping text
    }, 8000);

    return () => clearInterval(slideTimer);
  }, []);

  const slide = slides[currentSlide];

  return (
    <section
      className="hero hero-style pos-rel bg_img"
      style={{
        backgroundImage: `url(${heroBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat"
      }}
    >
      <style>{`
        .hero-style {
          padding-top: 260px !important;
          min-height: 1000px !important;
        }
        @media (max-width: 1199px) {
          .mobile-hero-title {
            font-family: 'Inter', sans-serif !important;
            font-size: 56px !important;
            line-height: 1.1 !important;
            font-weight: 700 !important;
            letter-spacing: -0.04em !important;
          }
        }
        @media (max-width: 991px) {
          .hero-style {
            padding-top: 130px !important;
            min-height: auto !important;
            padding-bottom: 200px !important;
          }
          .mobile-hero-title {
            font-size: 48px !important;
          }
        }
        @media (max-width: 767px) {
          .mobile-hero-title {
            font-size: 38px !important;
          }
        }
      `}</style>
      <div className="container">
        <div className="row">
          {/* ---------- Centered Content ---------- */}
          <div className="col-lg-12">
            <div className="hero-content text-center mx-auto wow fadeInUp" data-wow-delay="100ms" data-wow-duration="600ms">
              
              <div style={{ opacity: animate ? 1 : 0, transition: 'opacity 0.6s ease-in-out' }}>
                {/* Desktop Title */}
                <h1 className="title d-none d-xl-block">
                  {slide.title}
                </h1>

                {/* Mobile/Tablet Title */}
                <h1 className="title d-block d-xl-none mobile-hero-title">
                  {slide.title}
                </h1>
                
                <p className="sub-title mx-auto" style={{ maxWidth: "1000px", display: "block", marginTop: "30px" }}>
                  {slide.subtitle}
                </p>
                
                <div className="hero-btn d-flex flex-column flex-md-row justify-content-center align-items-center gap-3">
                  {slide.btn1Link.startsWith("/") ? (
                    <Link className="thm-btn agency-btn" to={slide.btn1Link}>
                      <span className="text">{slide.btn1Text}</span>
                      <span className="arrow">
                        <span className="arrow-icon">
                          {/* Right Arrow SVG */}
                          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="5.06592" y="19.9785" width="20.5712" height="2.61221" transform="rotate(-40.2798 5.06592 19.9785)" fill="white" />
                            <rect x="7.97095" y="7.24463" width="2.61221" height="2.61221" transform="rotate(-40.2798 7.97095 7.24463)" fill="white" />
                            <rect x="11.6523" y="7.54834" width="2.61221" height="2.61221" transform="rotate(-40.2798 11.6523 7.54834)" fill="white" />
                            <rect x="15.334" y="7.85205" width="2.61221" height="2.61221" transform="rotate(-40.2798 15.334 7.85205)" fill="white" />
                            <rect x="18.7119" y="11.8374" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.7119 11.8374)" fill="white" />
                            <rect x="18.4084" y="15.52" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.4084 15.52)" fill="white" />
                            <rect x="18.104" y="19.2012" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.104 19.2012)" fill="white" />
                          </svg>
                          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="5.06592" y="19.9785" width="20.5712" height="2.61221" transform="rotate(-40.2798 5.06592 19.9785)" fill="white" />
                            <rect x="7.97095" y="7.24463" width="2.61221" height="2.61221" transform="rotate(-40.2798 7.97095 7.24463)" fill="white" />
                            <rect x="11.6523" y="7.54834" width="2.61221" height="2.61221" transform="rotate(-40.2798 11.6523 7.54834)" fill="white" />
                            <rect x="15.334" y="7.85205" width="2.61221" height="2.61221" transform="rotate(-40.2798 15.334 7.85205)" fill="white" />
                            <rect x="18.7119" y="11.8374" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.7119 11.8374)" fill="white" />
                            <rect x="18.4084" y="15.52" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.4084 15.52)" fill="white" />
                            <rect x="18.104" y="19.2012" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.104 19.2012)" fill="white" />
                          </svg>
                        </span>
                      </span>
                    </Link>
                  ) : (
                    <a className="thm-btn agency-btn" href={slide.btn1Link} target="_blank" rel="noopener noreferrer">
                      <span className="text">{slide.btn1Text}</span>
                      <span className="arrow">
                        <span className="arrow-icon">
                          {/* Right Arrow SVG */}
                          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="5.06592" y="19.9785" width="20.5712" height="2.61221" transform="rotate(-40.2798 5.06592 19.9785)" fill="white" />
                            <rect x="7.97095" y="7.24463" width="2.61221" height="2.61221" transform="rotate(-40.2798 7.97095 7.24463)" fill="white" />
                            <rect x="11.6523" y="7.54834" width="2.61221" height="2.61221" transform="rotate(-40.2798 11.6523 7.54834)" fill="white" />
                            <rect x="15.334" y="7.85205" width="2.61221" height="2.61221" transform="rotate(-40.2798 15.334 7.85205)" fill="white" />
                            <rect x="18.7119" y="11.8374" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.7119 11.8374)" fill="white" />
                            <rect x="18.4084" y="15.52" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.4084 15.52)" fill="white" />
                            <rect x="18.104" y="19.2012" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.104 19.2012)" fill="white" />
                          </svg>
                          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="5.06592" y="19.9785" width="20.5712" height="2.61221" transform="rotate(-40.2798 5.06592 19.9785)" fill="white" />
                            <rect x="7.97095" y="7.24463" width="2.61221" height="2.61221" transform="rotate(-40.2798 7.97095 7.24463)" fill="white" />
                            <rect x="11.6523" y="7.54834" width="2.61221" height="2.61221" transform="rotate(-40.2798 11.6523 7.54834)" fill="white" />
                            <rect x="15.334" y="7.85205" width="2.61221" height="2.61221" transform="rotate(-40.2798 15.334 7.85205)" fill="white" />
                            <rect x="18.7119" y="11.8374" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.7119 11.8374)" fill="white" />
                            <rect x="18.4084" y="15.52" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.4084 15.52)" fill="white" />
                            <rect x="18.104" y="19.2012" width="2.61221" height="2.61221" transform="rotate(-40.2798 18.104 19.2012)" fill="white" />
                          </svg>
                        </span>
                      </span>
                    </a>
                  )}
                  
                  <Link className="thm-btn hero-outline-btn" to={slide.btn2Link}>
                    <span className="text">{slide.btn2Text}</span>
                  </Link>
                </div>
              </div>

              {/* Slide indicators */}
              <div className="d-flex justify-content-center align-items-center gap-2" style={{ marginTop: '80px' }}>
                {slides.map((_, idx) => (
                  <button 
                    key={idx} 
                    onClick={() => {
                      setAnimate(false);
                      setTimeout(() => {
                        setCurrentSlide(idx);
                        setAnimate(true);
                      }, 600);
                    }}
                    style={{ 
                      width: "12px", 
                      height: "12px", 
                      borderRadius: "50%", 
                      background: currentSlide === idx ? "#ba6be7" : "rgba(255,255,255,0.2)",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      transition: "background 0.3s ease"
                    }}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;

